import React, { useState, useEffect } from 'react';
import { hellosignApi } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import HelloSignPreview from '../../components/HelloSignPreview';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface Document {
    id: string;
    name: string;
    url: string;
    created_at: string;
    status: string;
    signature_request_id?: string;
    isGhost?: boolean;
}

interface BusinessDocumentManagementProps {
    businessId: string;
}

type TabType = 'pending' | 'completed';

const BusinessDocumentManagement: React.FC<BusinessDocumentManagementProps> = ({ businessId }) => {
    const { user } = useAuth();
    const [documents, setDocuments] = useState<Document[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [signerEmail, setSignerEmail] = useState('');
    const [signerName, setSignerName] = useState('');
    const [activeTab, setActiveTab] = useState<TabType>('pending');
    const [deleting, setDeleting] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
    const [documentData, setDocumentData] = useState<Document | null>(null);
    const [deletedDocumentIds, setDeletedDocumentIds] = useState<string[]>([]);
    const [hiddenDocumentIds, setHiddenDocumentIds] = useState<string[]>([]);
    const [documentSearchQuery, setDocumentSearchQuery] = useState('');
    const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    // Load hidden document IDs from localStorage on component mount
    useEffect(() => {
        const storedHiddenDocs = localStorage.getItem(`hiddenHelloSignDocs_${businessId}`);
        if (storedHiddenDocs) {
            setHiddenDocumentIds(JSON.parse(storedHiddenDocs));
        }
        
        const storedDeletedDocs = localStorage.getItem(`deletedHelloSignDocs_${businessId}`);
        if (storedDeletedDocs) {
            setDeletedDocumentIds(JSON.parse(storedDeletedDocs));
        }
    }, [businessId]);

    // Save hidden document IDs to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem(`hiddenHelloSignDocs_${businessId}`, JSON.stringify(hiddenDocumentIds));
    }, [hiddenDocumentIds, businessId]);
    
    // Save deleted document IDs to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem(`deletedHelloSignDocs_${businessId}`, JSON.stringify(deletedDocumentIds));
    }, [deletedDocumentIds, businessId]);

    useEffect(() => {
        loadDocuments();
    }, []);

    const loadDocuments = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // First, get documents from HelloSign API
            const response = await hellosignApi.listSignatureRequests();
            let signatureRequests = response.signature_requests;
            
            // Get a list of documents from our database to cross-check
            try {
                const businessDocsResponse = await axios.get(`${API_BASE_URL}/api/documents/business/${businessId}`);
                const businessDocs = businessDocsResponse.data;
                
                // Create a set of signature request IDs that exist in our database
                const validSignatureRequestIds = new Set(
                    businessDocs.map((doc: any) => doc.signature_request_id).filter(Boolean)
                );
                
                // Filter out HelloSign documents that don't exist in our database (ghost documents)
                signatureRequests = signatureRequests.filter(doc => validSignatureRequestIds.has(doc.id));
                
                // Filter out documents that we have marked as hidden
                signatureRequests = signatureRequests.filter(doc => !hiddenDocumentIds.includes(doc.id));
                
                // Also filter out documents that we know have been deleted
                signatureRequests = signatureRequests.filter(doc => !deletedDocumentIds.includes(doc.id));
            } catch (err) {
                console.warn("Could not cross-check with database documents, showing all HelloSign documents:", err);
            }
            
            setDocuments(signatureRequests);
        } catch (err) {
            console.error('Error loading documents:', err);
            setError('Failed to load documents');
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            setSelectedFile(event.target.files[0]);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile || !signerEmail || !signerName) {
            setError('Please select a file and provide signer details');
            return;
        }

        try {
            setUploading(true);
            setError(null);

            // Use the S3 upload endpoint directly with FormData
            const response = await hellosignApi.uploadAndSignDocument(
                selectedFile,
                signerEmail,
                signerName,
                selectedFile.name,
                businessId
            );

            // Refresh documents list
            await loadDocuments();

            // Reset form
            setSelectedFile(null);
            setSignerEmail('');
            setSignerName('');
            
            // Switch to pending tab to show the new document
            setActiveTab('pending');
        } catch (err) {
            console.error('Error uploading document:', err);
            setError('Failed to upload document');
        } finally {
            setUploading(false);
        }
    };

    const initiateDelete = async (documentId: string) => {
        const docToDelete = documents.find(doc => doc.id === documentId);
        setDocumentToDelete(documentId);
        setDocumentData(docToDelete || null);
        setShowDeleteConfirm(true);
    };

    const handleDelete = async () => {
        if (!documentToDelete) return;
        
        try {
            setDeleting(documentToDelete);
            setError(null);
            
            // First, optimistically update the UI by removing the document
            setDocuments(prevDocuments => prevDocuments.filter(doc => doc.id !== documentToDelete));
            
            // Add this ID to our locally tracked deleted documents
            setDeletedDocumentIds(prev => [...prev, documentToDelete]);
            
            // Try deletion through API
            try {
                await axios.delete(`${API_BASE_URL}/api/documents/hellosign/${documentToDelete}`);
            } catch (err) {
                console.error("Error in first delete attempt:", err);
                
                // If HelloSign endpoint fails, try direct document deletion
                try {
                    // Get documents for this business to find matching document
                    const businessDocsResponse = await axios.get(`${API_BASE_URL}/api/documents/business/${businessId}`);
                    const businessDocs = businessDocsResponse.data;
                    
                    // Find document that matches this signature request ID
                    const matchingDoc = businessDocs.find((doc: any) => doc.signature_request_id === documentToDelete);
                    
                    if (matchingDoc) {
                        await axios.delete(`${API_BASE_URL}/api/documents/${matchingDoc.id}`);
                    } else {
                        // Last resort - try direct delete with the ID we have
                        await axios.delete(`${API_BASE_URL}/api/documents/${documentToDelete}`);
                    }
                } catch (secondErr) {
                    console.error("All delete attempts failed:", secondErr);
                    // Still keep the document deleted from UI
                }
            }
        } catch (err) {
            console.error('Error deleting document:', err);
            setError('Failed to delete document');
            
            // Even if deletion fails at the server level, keep it removed from UI
        } finally {
            setDeleting(null);
            setShowDeleteConfirm(false);
            setDocumentToDelete(null);
            setDocumentData(null);
        }
    };

    const hideDocument = (documentId: string) => {
        setHiddenDocumentIds(prev => [...prev, documentId]);
        setDocuments(prevDocuments => prevDocuments.filter(doc => doc.id !== documentId));
    };

    const resetHiddenDocuments = () => {
        setHiddenDocumentIds([]);
        loadDocuments();
    };
    
    // Filter documents based on the active tab and search query
    const filteredDocuments = documents.filter(doc => {
        // First filter by tab
        const matchesTab = activeTab === 'completed' 
            ? doc.status === 'completed'
            : doc.status === 'pending';
            
        // Then filter by search query if one exists
        const matchesSearch = !documentSearchQuery || 
            doc.name.toLowerCase().includes(documentSearchQuery.toLowerCase());
            
        return matchesTab && matchesSearch;
    });
    
    // Format date for display
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const handlePreview = (documentId: string) => {
        setPreviewDocumentId(documentId);
        setShowPreviewModal(true);
    };

    const closePreviewModal = () => {
        setPreviewDocumentId(null);
        setShowPreviewModal(false);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                <p className="ml-3 text-gray-600">Loading documents...</p>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="mb-8">
                <h2 className="text-2xl font-bold mb-4">Document Management</h2>
                
                {/* Upload Form */}
                <div className="bg-white p-6 rounded-lg border mb-6">
                    <h3 className="text-lg font-medium mb-4">Upload New Document</h3>
                    
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Document File
                            </label>
                            <input
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={handleFileSelect}
                                className="block w-full text-sm text-gray-500
                                    file:mr-4 file:py-2 file:px-4
                                    file:rounded file:border-0
                                    file:text-sm file:font-semibold
                                    file:bg-purple-50 file:text-purple-700
                                    hover:file:bg-purple-100"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Signer Email
                            </label>
                            <input
                                type="email"
                                value={signerEmail}
                                onChange={(e) => setSignerEmail(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                                placeholder="signer@example.com"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Signer Name
                            </label>
                            <input
                                type="text"
                                value={signerName}
                                onChange={(e) => setSignerName(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                                placeholder="John Doe"
                            />
                        </div>

                        <button
                            onClick={handleUpload}
                            disabled={uploading || !selectedFile || !signerEmail || !signerName}
                            className={`w-full px-4 py-2 rounded-md text-white font-medium
                                ${uploading || !selectedFile || !signerEmail || !signerName
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-purple-600 hover:bg-purple-700'
                                }`}
                        >
                            {uploading ? 'Uploading...' : 'Upload and Send for Signature'}
                        </button>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded">
                        {error}
                    </div>
                )}

                {/* Document Tabs */}
                <div className="bg-white rounded-lg border">
                    <div className="border-b flex flex-col sm:flex-row justify-between">
                        <div className="flex">
                            <button
                                className={`px-4 py-2 font-medium text-sm ${
                                    activeTab === 'pending' 
                                    ? 'border-b-2 border-purple-600 text-purple-600' 
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                                onClick={() => setActiveTab('pending')}
                            >
                                Pending Documents
                            </button>
                            <button
                                className={`px-4 py-2 font-medium text-sm ${
                                    activeTab === 'completed' 
                                    ? 'border-b-2 border-purple-600 text-purple-600' 
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                                onClick={() => setActiveTab('completed')}
                            >
                                Completed Documents
                            </button>
                        </div>
                        
                        <div className="px-4 py-2 flex items-center space-x-2">
                            {hiddenDocumentIds.length > 0 && (
                                <button
                                    onClick={resetHiddenDocuments}
                                    className="text-sm text-purple-600 hover:text-purple-800"
                                >
                                    Show Hidden ({hiddenDocumentIds.length})
                                </button>
                            )}
                            <input
                                type="search"
                                placeholder="Search documents..."
                                value={documentSearchQuery}
                                onChange={(e) => setDocumentSearchQuery(e.target.value)}
                                className="px-3 py-1 border border-gray-300 rounded text-sm"
                            />
                        </div>
                    </div>
                    
                    {/* Documents Table */}
                    {filteredDocuments.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">
                            No {activeTab} documents found
                            {documentSearchQuery && " matching your search"}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Document Name
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date Created
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredDocuments.map((doc) => (
                                        <tr key={doc.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{doc.name}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-500">{formatDate(doc.created_at)}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                    doc.status === 'completed' 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                    {doc.status === 'completed' ? 'Completed' : 'Pending'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                {doc.status === 'completed' && (
                                                    <button
                                                        onClick={() => handlePreview(doc.id)}
                                                        className="text-blue-600 hover:text-blue-900 mr-3"
                                                    >
                                                        Preview
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => initiateDelete(doc.id)}
                                                    disabled={deleting === doc.id}
                                                    className={`${
                                                        deleting === doc.id
                                                        ? 'text-gray-400 cursor-not-allowed'
                                                        : 'text-red-600 hover:text-red-900'
                                                    }`}
                                                >
                                                    {deleting === doc.id ? 'Deleting...' : 'Delete'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
                        <h3 className="text-lg font-medium mb-4 text-gray-900">Confirm Deletion</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Are you sure you want to delete this document? This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                className="px-4 py-2 rounded text-gray-700 border border-gray-300 hover:bg-gray-100"
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setDocumentToDelete(null);
                                    setDocumentData(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
                                onClick={handleDelete}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {showPreviewModal && previewDocumentId && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 max-w-6xl w-full h-5/6 flex flex-col">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-gray-900">Document Preview</h3>
                            <button
                                onClick={closePreviewModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="flex-grow overflow-hidden border rounded">
                            {/* Direct iframe to the document preview endpoint */}
                            <iframe 
                                src={`${API_BASE_URL}/api/documents/hellosign/${previewDocumentId}/preview`}
                                className="w-full h-full"
                                title="Document Preview"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BusinessDocumentManagement; 